import { CalendarEvent } from '@zayan-erp/shared';

export enum DsmAppointmentType {
  DesignConsultation = 1,
  OptionsSelection = 2,
  Contract = 3
}

export const DSM_APPOINTMENT_TYPE_LABELS: Record<DsmAppointmentType, string> = {
  [DsmAppointmentType.DesignConsultation]: 'Design Consultation',
  [DsmAppointmentType.OptionsSelection]: 'Options Selection',
  [DsmAppointmentType.Contract]: 'Contract'
};

export enum DsmAppointmentStatus {
  Scheduled = 1,
  Completed = 2,
  Cancelled = 3,
  NoShow = 4,
  ReschedulingNeeded = 5
}

export const DSM_APPOINTMENT_STATUS_LABELS: Record<DsmAppointmentStatus, string> = {
  [DsmAppointmentStatus.Scheduled]: 'Scheduled',
  [DsmAppointmentStatus.Completed]: 'Completed',
  [DsmAppointmentStatus.Cancelled]: 'Cancelled',
  [DsmAppointmentStatus.NoShow]: 'No-show',
  [DsmAppointmentStatus.ReschedulingNeeded]: 'Rescheduling needed'
};

export interface DsmAppointment {
  id: string;
  dealId: string;
  lotId: string;
  lotNo: string;
  projectId: string;
  projectName: string;
  modelId: string | null;
  modelName: string | null;
  elevationId: string | null;
  elevationCode: string | null;
  buyerName: string;
  startDateTime: string;
  endDateTime: string;
  durationMinutes: number;
  appointmentType: DsmAppointmentType;
  status: DsmAppointmentStatus;
  locationId: string;
  locationAddressLine1: string;
  locationCityName: string;
  assignedUserId: string;
  assignedUserName: string;
  actualStartDateTime: string | null;
  actualEndDateTime: string | null;
  notes: string | null;
  createdAt: string;
  stepIds: string[];
  steps: DsmAppointmentStepRef[];
  rebookedFromId: string | null;
  rebookedFromStartDateTime: string | null;
  rebookedToId: string | null;
  rebookedToStartDateTime: string | null;
}

export interface DsmAppointmentStepRef {
  stepId: string;
  name: string;
  sortOrder: number;
  scenarioId: string;
  scenarioName: string;
}

export type DsmAppointmentUpdatePayload = Pick<
  DsmAppointment,
  'startDateTime' | 'durationMinutes' | 'appointmentType' | 'locationId' | 'assignedUserId' | 'stepIds' | 'notes'
>;

export type DsmAppointmentCreatePayload = DsmAppointmentUpdatePayload &
  Pick<DsmAppointment, 'dealId'> & { rebookedFromId?: string };

export interface DsmAppointmentQuery {
  salesOfficeId?: string | null;
  consultantId?: string | null;
  projectId?: string | null;
  lotId?: string | null;
  dealId?: string | null;
  from?: string | null;
  to?: string | null;
  statuses?: DsmAppointmentStatus[];
  types?: DsmAppointmentType[];
}

export interface DsmAppointmentSummary {
  todayCount: number;
  overdueCount: number;
  weekCount: number;
  rescheduleCount: number;
  nextAppointment: DsmAppointment | null;
}

export interface DsmAppointmentLocation {
  id: string;
  addressLine1: string;
  addressLine2: string | null;
  suite: string | null;
  cityId: string;
  cityName: string;
  postalCode: string | null;
}

export type DsmAppointmentLocationPayload = Omit<DsmAppointmentLocation, 'id' | 'cityName'>;

export type DsmAppointmentDeal = Pick<
  DsmAppointment,
  'dealId' | 'lotId' | 'lotNo' | 'projectId' | 'projectName' | 'buyerName' | 'modelName' | 'elevationCode'
>;

export type DsmAppointmentPrefill = Pick<
  DsmAppointment,
  'appointmentType' | 'stepIds' | 'assignedUserId' | 'locationId' | 'notes'
> & {
  deal: DsmAppointmentDeal;
  start: Date | null;
  end: Date | null;
};

export interface DsmAppointmentBooking {
  appointment: DsmAppointment;
  start: Date;
  end: Date;
}

export interface DsmAppointmentWeekDay {
  date: Date;
  isPast: boolean;
  isSelected: boolean;
  bookedCount: number;
}

export enum DsmAppointmentAgendaKind {
  Booked = 'booked',
  New = 'new',
  Overlap = 'overlap'
}

export interface DsmAppointmentAgendaRow {
  key: string;
  kind: DsmAppointmentAgendaKind;
  start: Date;
  end: Date;
  appointment: DsmAppointment | null;
}

// ─── Appointments list ───────────────────────────────────────────────────────

export interface DsmAppointmentRow {
  appointment: DsmAppointment;
  start: Date;
  end: Date;
  stepNames: string[];
  isToday: boolean;
  isOverdue: boolean;
  isInProgress: boolean;
  /** Null when the tab's status already says it all and there are no actual times to show. */
  statusTag: { label: string; severity: 'info' | 'success' | 'secondary' | 'danger' | 'warn' } | null;
  /** When it actually started and ended, shown on hovering the status tag. */
  actualTimes: string | null;
  showRebook: boolean;
  hasActions: boolean;
}

export enum DsmAppointmentGroupKey {
  Overdue = 'overdue',
  Today = 'today',
  NextWeek = 'next-week',
  Later = 'later',
  LastWeek = 'last-week',
  Earlier = 'earlier',
  All = 'all'
}

export interface DsmAppointmentRowGroup {
  key: DsmAppointmentGroupKey;
  from: Date | null;
  to: Date | null;
  isOpen: boolean;
  rows: DsmAppointmentRow[];
}

export interface DsmAppointmentCalendarEvent extends CalendarEvent {
  classNames: string[];
  extendedProps: {
    timeLabel: string;
    subLabel: string;
    isReschedulingNeeded: boolean;
  };
}
